import * as THREE from 'three';
import type { CharacterPreviewRig } from './preview-rig';

function isStatusEffectObject(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (typeof current.userData.characterStatusEffect === 'string') return true;
    current = current.parent;
  }
  return false;
}

function isEyeObject(object: THREE.Object3D): boolean {
  let current: THREE.Object3D | null = object;
  while (current != null) {
    if (current.userData.characterEyes === true) return true;
    current = current.parent;
  }
  return false;
}

export interface CharacterRigOpacityOptions {
  includeEyes?: boolean;
}

let ghostBodyAlphaMap: THREE.DataTexture | null = null;

export function setCharacterRigOpacity(
  rig: CharacterPreviewRig,
  opacity: number,
  options: CharacterRigOpacityOptions = {},
): void {
  const includeEyes = options.includeEyes === true;
  rig.root.traverse((obj) => {
    if (isStatusEffectObject(obj)) return;
    if (!includeEyes && isEyeObject(obj)) return;
    const mesh = obj as THREE.Mesh;
    const material = mesh.material;
    if (material == null) return;
    const materials = Array.isArray(material) ? material : [material];
    const isSolid = opacity >= 0.999;
    for (const mat of materials) {
      mat.transparent = !isSolid;
      mat.opacity = opacity;
      mat.depthWrite = isSolid;
      mat.needsUpdate = true;
    }
  });
}

export function setCharacterRigEyesVisible(rig: CharacterPreviewRig, visible: boolean): void {
  rig.root.traverse((obj) => {
    if (!isEyeObject(obj)) return;
    obj.visible = visible;
  });
}

export function setCharacterRigColorableTint(rig: CharacterPreviewRig, color: number): void {
  const meshes = getColorableMeshes(rig);
  for (const mesh of meshes) {
    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      if (!('color' in mat)) continue;
      (mat as unknown as { color: THREE.Color }).color.setHex(color);
      mat.needsUpdate = true;
    }
  }
}

export function setCharacterRigColorableOpacity(rig: CharacterPreviewRig, opacity: number): void {
  const isSolid = opacity >= 0.999;
  for (const mesh of getColorableMeshes(rig)) {
    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      mat.transparent = !isSolid;
      mat.opacity = opacity;
      mat.depthWrite = isSolid;
      mat.needsUpdate = true;
    }
  }
}

export function setCharacterRigColorableGhostOpacity(
  rig: CharacterPreviewRig,
  opacity: number,
): void {
  const alphaMap = getGhostBodyAlphaMap();
  for (const mesh of getColorableMeshes(rig)) {
    const material = mesh.material;
    const materials = Array.isArray(material) ? material : [material];
    for (const mat of materials) {
      mat.transparent = true;
      mat.opacity = opacity;
      mat.depthWrite = false;
      if ('alphaMap' in mat) {
        (mat as unknown as { alphaMap: THREE.Texture | null }).alphaMap = alphaMap;
      }
      if ('alphaHash' in mat) {
        (mat as unknown as { alphaHash: boolean }).alphaHash = true;
      }
      mat.needsUpdate = true;
    }
  }
}

export function getCharacterRigOpacity(rig: CharacterPreviewRig): number {
  let opacity = 1;
  rig.root.traverse((obj) => {
    if (isStatusEffectObject(obj)) return;
    if (isEyeObject(obj)) return;
    const mesh = obj as THREE.Mesh;
    const material = mesh.material;
    if (material == null) return;
    const mat = Array.isArray(material) ? material[0] : material;
    if (mat != null) opacity = mat.opacity;
  });
  return opacity;
}

function getColorableMeshes(rig: CharacterPreviewRig): THREE.Mesh[] {
  return rig.colorableMeshes != null && rig.colorableMeshes.length > 0
    ? rig.colorableMeshes
    : [rig.bodyMesh];
}

function getGhostBodyAlphaMap(): THREE.DataTexture {
  if (ghostBodyAlphaMap !== null) return ghostBodyAlphaMap;

  const size = 16;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const index = (y * size + x) * 4;
      const value = ghostAlphaValue(x, y);
      data[index] = value;
      data[index + 1] = value;
      data[index + 2] = value;
      data[index + 3] = value;
    }
  }

  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.minFilter = THREE.NearestFilter;
  texture.magFilter = THREE.NearestFilter;
  texture.needsUpdate = true;
  ghostBodyAlphaMap = texture;
  return texture;
}

function ghostAlphaValue(x: number, y: number): number {
  const hash = (x * 73 + y * 151 + ((x * y) % 37) * 19) % 256;
  if (hash < 74) return 255;
  if (hash < 102) return 160;
  if ((x + y) % 7 === 0) return 96;
  return 0;
}
